import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

const ROUTE_LABELS = {
  "home": { label: "Home", path: "/" },
  "about": { label: "Our Story", path: "/about" },
  "menu-combos": { label: "Combo Meals", path: "/menu/combos" },
  "menu-a-la-carte": { label: "À La Carte", path: "/menu/a-la-carte" },
  "catering": { label: "Catering", path: "/catering" },
  "featured": { label: "Food Bloggers", path: "/featured" },
  "blog": { label: "Blog", path: "/blog" },
  "contact": { label: "Contact", path: "/contact" },
};

const EMPTY = { title: "", description: "", keywords: "", og_image: "", canonical: "" };

export default function SeoEditor() {
  const [pages, setPages] = useState([]);
  const [activeRoute, setActiveRoute] = useState("home");
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/seo");
      setPages(data);
      const cur = data.find((p) => p.route === activeRoute) || data[0];
      if (cur) {
        setActiveRoute(cur.route);
        setDraft({
          title: cur.title || "",
          description: cur.description || "",
          keywords: cur.keywords || "",
          og_image: cur.og_image || "",
          canonical: cur.canonical || "",
        });
      }
    } catch {
      toast.error("Failed to load SEO settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectRoute = (route) => {
    const p = pages.find((x) => x.route === route);
    setActiveRoute(route);
    setDraft({
      title: p?.title || "",
      description: p?.description || "",
      keywords: p?.keywords || "",
      og_image: p?.og_image || "",
      canonical: p?.canonical || "",
    });
  };

  const update = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/seo/${activeRoute}`, draft);
      toast.success("Saved");
      setPages((list) => list.map((p) => (p.route === activeRoute ? data : p)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/seo/${activeRoute}/reset`);
      toast.success("Reset to defaults");
      setPages((list) => list.map((p) => (p.route === activeRoute ? data : p)));
      setDraft({
        title: data.title || "",
        description: data.description || "",
        keywords: data.keywords || "",
        og_image: data.og_image || "",
        canonical: data.canonical || "",
      });
    } catch {
      toast.error("Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const meta = ROUTE_LABELS[activeRoute] || { label: activeRoute, path: "/" };
  const titleLen = draft.title.length;
  const descLen = draft.description.length;

  if (loading) return <p className="text-neutral-400 text-lg">Loading…</p>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-5" data-testid="seo-editor">
      {/* Left rail */}
      <aside className="bg-[#141414] border border-white/10 rounded-2xl p-3 h-fit">
        <p className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-500">
          Edit page meta
        </p>
        {pages.map((p) => (
          <button
            key={p.route}
            data-testid={`seo-route-${p.route}`}
            onClick={() => selectRoute(p.route)}
            className={`w-full text-left px-3 py-3 rounded-xl transition-colors ${
              activeRoute === p.route
                ? "bg-[#ffd100] text-[#0a0a0a]"
                : "text-neutral-200 hover:bg-white/5"
            }`}
          >
            <p className="text-sm font-semibold">{ROUTE_LABELS[p.route]?.label || p.route}</p>
            <p className={`text-xs mt-1 truncate ${activeRoute === p.route ? "text-[#0a0a0a]/70" : "text-neutral-500"}`}>
              {ROUTE_LABELS[p.route]?.path || "/" + p.route}
            </p>
          </button>
        ))}
      </aside>

      {/* Editor */}
      <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 md:p-7">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
          <div>
            <p className="text-[#ffd100] text-xs font-bold uppercase tracking-[0.16em]">Editing meta for</p>
            <h2 className="font-display text-2xl md:text-3xl text-white mt-1">{meta.label}</h2>
          </div>
          <a
            href={meta.path}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 border-2 border-white/20 text-white font-semibold px-4 py-2 rounded-full hover:border-[#ffd100] hover:text-[#ffd100] transition-colors text-sm"
          >
            View page <ExternalLink size={14} />
          </a>
        </div>

        {/* SERP Preview */}
        <div className="bg-white rounded-xl p-5 mb-6" data-testid="serp-preview">
          <div className="text-[13px] text-[#4a4a4a] mb-1 truncate">{draft.canonical || `https://www.shaancurryhouse.com${meta.path}`}</div>
          <div className="text-[#1a0dab] text-[20px] leading-tight font-medium truncate hover:underline">
            {draft.title || "Page title"}
          </div>
          <div className="text-[#4a4a4a] text-[14px] leading-snug mt-1 line-clamp-2">
            {draft.description || "Meta description shows here."}
          </div>
        </div>

        {/* Fields */}
        <div className="space-y-5">
          <Field label="Page title" hint="Shows in the browser tab and as the SERP headline. Aim for 50–60 characters.">
            <input
              data-testid="seo-title-input"
              value={draft.title}
              onChange={update("title")}
              maxLength={100}
              className="w-full bg-[#0a0a0a] border border-white/20 rounded-lg px-4 py-3 text-white text-base focus:border-[#ffd100] outline-none"
            />
            <Counter len={titleLen} min={50} max={60} />
          </Field>

          <Field label="Meta description" hint="Sentence Google shows under the title. Aim for 140–160 characters.">
            <textarea
              data-testid="seo-description-input"
              value={draft.description}
              onChange={update("description")}
              rows={3}
              maxLength={320}
              className="w-full bg-[#0a0a0a] border border-white/20 rounded-lg px-4 py-3 text-white text-base focus:border-[#ffd100] outline-none resize-none"
            />
            <Counter len={descLen} min={140} max={160} />
          </Field>

          <Field label="Keywords" hint="Comma-separated. Optional — modern Google largely ignores this but it can help other engines.">
            <input
              data-testid="seo-keywords-input"
              value={draft.keywords}
              onChange={update("keywords")}
              className="w-full bg-[#0a0a0a] border border-white/20 rounded-lg px-4 py-3 text-white text-base focus:border-[#ffd100] outline-none"
            />
          </Field>

          <Field label="Social share image (Open Graph)" hint="Full image URL shown when the page is shared on Facebook, WhatsApp, Twitter, etc. Recommended 1200×630.">
            <input
              data-testid="seo-ogimage-input"
              value={draft.og_image}
              onChange={update("og_image")}
              placeholder="https://..."
              className="w-full bg-[#0a0a0a] border border-white/20 rounded-lg px-4 py-3 text-white text-base focus:border-[#ffd100] outline-none"
            />
            {draft.og_image && (
              <img
                src={draft.og_image}
                alt="OG preview"
                className="mt-3 rounded-lg border border-white/10 max-w-md w-full"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            )}
          </Field>

          <Field label="Canonical URL" hint="The single URL Google should treat as authoritative for this page. Usually just the page's own URL.">
            <input
              data-testid="seo-canonical-input"
              value={draft.canonical}
              onChange={update("canonical")}
              className="w-full bg-[#0a0a0a] border border-white/20 rounded-lg px-4 py-3 text-white text-base focus:border-[#ffd100] outline-none"
            />
          </Field>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            data-testid="seo-save-btn"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#ffd100] text-[#0a0a0a] font-bold px-6 py-3 rounded-full hover:bg-[#e5bc00] transition-colors disabled:opacity-60"
          >
            <Save size={16} /> {saving ? "Saving…" : "Save changes"}
          </button>
          <button
            data-testid="seo-reset-btn"
            onClick={reset}
            disabled={saving}
            className="inline-flex items-center gap-2 border-2 border-white/20 text-white font-semibold px-6 py-3 rounded-full hover:border-[#ffd100] hover:text-[#ffd100] transition-colors disabled:opacity-60"
          >
            <RotateCcw size={16} /> Reset to defaults
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-neutral-200 mb-1">{label}</label>
      {hint && <p className="text-xs text-neutral-500 mb-2">{hint}</p>}
      {children}
    </div>
  );
}

function Counter({ len, min, max }) {
  const good = len >= min && len <= max;
  const color = good ? "text-emerald-400" : len > max ? "text-red-400" : "text-neutral-500";
  return (
    <p className={`text-xs mt-1.5 ${color}`}>
      {len} characters {good ? "✓" : `(recommended ${min}–${max})`}
    </p>
  );
}
